import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/AuthContext';
import { API_BASE, s, authHeaders, fmtTime, parseDevice } from './sharedStyles';

export default function SessionsPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(0);
  const [tenants, setTenants] = useState([]);

  const [filterTenantId, setFilterTenantId] = useState('');
  const [filterUserEmail, setFilterUserEmail] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/admin/tenants`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : [])
      .then(setTenants)
      .catch((e) => console.error('Tenants fetch failed', e));
  }, [token]);

  const fetchSessions = useCallback((page, filters) => {
    const params = new URLSearchParams({ page: String(page), size: '25' });
    if (filters?.tenantId) params.set('tenantId', filters.tenantId);
    if (filters?.userEmail) params.set('userEmail', filters.userEmail);
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    fetch(`${API_BASE}/api/v1/admin/sessions?${params.toString()}`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : { content: [], totalElements: 0 })
      .then((data) => {
        setSessions(data.content || []);
        setSessionsTotal(data.totalElements || 0);
        setSessionsPage(page);
      })
      .catch((e) => console.error('Sessions fetch failed', e));
  }, [token]);

  useEffect(() => { fetchSessions(0, {}); }, [fetchSessions]);

  const currentFilters = { tenantId: filterTenantId, userEmail: filterUserEmail, from: filterFrom, to: filterTo };

  const applyFilter = (e) => {
    if (e) e.preventDefault();
    fetchSessions(0, currentFilters);
  };

  const clearFilter = () => {
    setFilterTenantId('');
    setFilterUserEmail('');
    setFilterFrom('');
    setFilterTo('');
    fetchSessions(0, {});
  };

  return (
    <div>
      <main style={s.main}>
        <h1 style={s.pageTitle}>{t('monitor.sections.sessions')}</h1>

        <form onSubmit={applyFilter} style={s.filterBar}>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>{t('monitor.sessions.filterByTenant')}</label>
            <select style={s.filterSelect} value={filterTenantId} onChange={(e) => setFilterTenantId(e.target.value)}>
              <option value="">{t('monitor.sessions.allTenants', 'All tenants')}</option>
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>{tn.name}</option>
              ))}
            </select>
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>{t('monitor.sessions.filterByUser')}</label>
            <input type="text" style={s.filterInput} placeholder="user@example.com"
              value={filterUserEmail} onChange={(e) => setFilterUserEmail(e.target.value)} />
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>{t('monitor.sessions.filterFrom')}</label>
            <input type="date" style={s.filterInput} value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>{t('monitor.sessions.filterTo')}</label>
            <input type="date" style={s.filterInput} value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
          </div>
          <button type="submit" style={s.primaryBtn}>{t('monitor.sessions.applyFilter')}</button>
          <button type="button" style={s.outlineBtn} onClick={clearFilter}>{t('monitor.sessions.clearFilter')}</button>
        </form>

        <div style={{ ...s.card, overflowX: 'auto' }}>
          <div style={s.cardTitle}>{t('monitor.sessions.title', { total: sessionsTotal })}</div>
          {sessions.length === 0 && <div style={{ color: '#64748B' }}>{t('monitor.sessions.noSessions')}</div>}
          {sessions.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  {[t('monitor.sessions.email'), t('monitor.sessions.tenant'), t('monitor.sessions.ip'), t('monitor.sessions.device'), t('monitor.sessions.login'), t('monitor.sessions.logout')].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid #334155', color: '#94A3B8', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessions.map((sess) => (
                  <tr key={sess.id}>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #1E293B', color: '#F8FAFC' }}>{sess.email}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #1E293B', color: '#CBD5E1' }}>{sess.tenantName}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #1E293B', color: '#CBD5E1', fontFamily: 'monospace' }}>{sess.ipAddress}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #1E293B', color: '#94A3B8' }} title={sess.userAgent}>{parseDevice(sess.userAgent)}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #1E293B', color: '#CBD5E1', whiteSpace: 'nowrap' }}>{fmtTime(sess.loggedInAt)}</td>
                    <td style={{ padding: '8px 6px', borderBottom: '1px solid #1E293B' }}>
                      {sess.loggedOutAt
                        ? <span style={{ color: '#94A3B8' }}>{fmtTime(sess.loggedOutAt)}</span>
                        : <span style={{ color: '#10B981', fontWeight: 700 }}>{t('monitor.sessions.active')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {sessionsTotal > 25 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
              <button style={s.outlineBtn} disabled={sessionsPage === 0} onClick={() => fetchSessions(sessionsPage - 1, currentFilters)}>{t('monitor.sessions.prev')}</button>
              <span style={{ color: '#94A3B8', fontSize: '0.85rem', padding: '0.5rem' }}>
                {t('monitor.sessions.pageOf', { page: sessionsPage + 1, total: Math.ceil(sessionsTotal / 25) })}
              </span>
              <button style={s.outlineBtn} disabled={(sessionsPage + 1) * 25 >= sessionsTotal} onClick={() => fetchSessions(sessionsPage + 1, currentFilters)}>{t('monitor.sessions.next')}</button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
